import { Trans, t } from '@lingui/macro'
import EditIcon from '@mui/icons-material/Edit'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from '@mui/material'
import { withRouter } from '@om1/platform-routing'
import { RoutedFrameworkComponentProps, connectRedux } from '@om1/platform-utils'
import { useState } from 'react'
import { BackOfficeState } from '../state'
import { OrganizationMetadataState, dataDeliveryActions } from '../state/data-delivery'
import { createCreateEditOrganizationMetadataDialogComponent } from './CreateEditOrganizationMetadataDialog'

export type OrganizationMetadataListModalProps = RoutedFrameworkComponentProps<
    {},
    { organizationId: string },
    { organizationMetadata: OrganizationMetadataState },
    Pick<typeof dataDeliveryActions, 'getOrganizationMetadata' | 'createOrganizationMetadata' | 'updateOrganizationMetadata'>,
    {
        open: boolean
        onClose: () => void
        orgMetadata?: any[]
    }
>
const CreateEditOrganizationMetadataDialog = createCreateEditOrganizationMetadataDialogComponent()
export function OrganizationMetadataListModal({ state, actions, routing, props }: OrganizationMetadataListModalProps) {
    const [addOpen, setAddOpen] = useState(false)
    const [editData, setEditData] = useState<{ type: 'DATA_STUDIO' | 'S3_INTERNAL' | 'S3_EXTERNAL' } | null>(null)
    const { open, onClose } = props
    const { organizationMetadata } = state

    // Define constants inside the function
    const S3_INTERNAL_LABEL = t`S3 Internal`
    const S3_EXTERNAL_LABEL = t`S3 External`
    const DATA_STUDIO_LABEL = t`Data Studio`

    const handleEdit = (connectionType: string) => {
        let deliveryType: 'DATA_STUDIO' | 'S3_INTERNAL' | 'S3_EXTERNAL' = 'DATA_STUDIO'

        if (connectionType === S3_INTERNAL_LABEL) {
            deliveryType = 'S3_INTERNAL'
        } else if (connectionType === S3_EXTERNAL_LABEL) {
            deliveryType = 'S3_EXTERNAL'
        } else if (connectionType === DATA_STUDIO_LABEL) {
            deliveryType = 'DATA_STUDIO'
        }

        setEditData({ type: deliveryType })
        setAddOpen(true)
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
                <DialogTitle>
                    <Trans>Connections</Trans>
                </DialogTitle>
                <DialogContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Trans>Connection Type</Trans>
                                </TableCell>
                                <TableCell>
                                    <Trans>Attributes</Trans>
                                </TableCell>
                                <TableCell>
                                    <Trans>Edit</Trans>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.keys(organizationMetadata.metadata).length > 0 ? (
                                [S3_INTERNAL_LABEL, S3_EXTERNAL_LABEL, DATA_STUDIO_LABEL].map((key) => {
                                    let data = {}
                                    if (key === S3_INTERNAL_LABEL) {
                                        data = Object.fromEntries(
                                            Object.entries(organizationMetadata.metadata).filter(
                                                ([k, v]) => k.startsWith('deliveryAwsInternal') && v != null && v !== ''
                                            )
                                        )
                                        if (Object.keys(data).length === 0) return null
                                    }
                                    if (key === S3_EXTERNAL_LABEL) {
                                        data = Object.fromEntries(
                                            Object.entries(organizationMetadata.metadata).filter(
                                                ([k, v]) => k.startsWith('deliveryAwsExternal') && v != null && v !== ''
                                            )
                                        )
                                        if (Object.keys(data).length === 0) return null
                                    }
                                    if (key === DATA_STUDIO_LABEL) {
                                        data = {
                                            deliveryDatabase: organizationMetadata.metadata.deliveryDatabase,
                                            deliveryShareName: organizationMetadata.metadata.deliveryShareName
                                        }
                                        // Hide row if both are empty/null/undefined
                                    }
                                    return (
                                        <TableRow key={key}>
                                            <TableCell>{key}</TableCell>
                                            <TableCell>
                                                <pre style={{ fontSize: 12 }}>{JSON.stringify(data, null, 2)}</pre>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleEdit(key)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} align='center'>
                                        <Trans>No connections found</Trans>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>
                        <Trans>Close</Trans>
                    </Button>
                    <Button
                        variant='contained'
                        onClick={() => {
                            setAddOpen(true)
                        }}
                    >
                        <Trans>Add Connection</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
            {addOpen && (
                <CreateEditOrganizationMetadataDialog
                    onCancel={() => {
                        setAddOpen(false)
                        setEditData(null)
                    }}
                    onSave={() => {
                        setAddOpen(false)
                        setEditData(null)
                    }}
                    initialDeliveryType={editData?.type}
                />
            )}
        </>
    )
}

export function createOrganizationMetadataListModalComponent<TState extends { backOffice: BackOfficeState }>() {
    return connectRedux(
        withRouter(OrganizationMetadataListModal, { namespace: 'organizationMetadata', dataFetchAction: 'getOrganizationMetadata' }),
        (state: TState) => ({
            organizationMetadata: state.backOffice.dataDelivery.organizationMetadata
        }),
        {
            getOrganizationMetadata: dataDeliveryActions.getOrganizationMetadata,
            createOrganizationMetadata: dataDeliveryActions.createOrganizationMetadata,
            updateOrganizationMetadata: dataDeliveryActions.updateOrganizationMetadata
        }
    )
}

export type OrganizationMetadataListModalConnected = ReturnType<typeof createOrganizationMetadataListModalComponent>
